// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ContentChild,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  Renderer2,
  TemplateRef,
  ViewChild,
} from '@angular/core';

@Component({
  standalone: true,
  imports: [CommonModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-dna-syngovia-carousel',
  templateUrl: './dna-syngovia-carousel.component.html',
  styleUrls: ['./dna-syngovia-carousel.component.scss'],
})
export class DnaSyngoviaCarouselComponent implements AfterViewInit, OnDestroy {
  @Input({ required: true }) carouselData: unknown[] = [];

  @ViewChild('carouselContainer') carouselContainer:
    | ElementRef<HTMLElement>
    | undefined;

  @ContentChild('carouselItem') carouselItemTemplate:
    | TemplateRef<unknown>
    | undefined;

  private visibleElements: Element[] = [];

  private unlistenScrollFn: (() => void) | undefined = undefined;
  private readonly unlistenItemClickFns: (() => void)[] = [];
  private readonly GAP_OFFSET = 8;

  public constructor(private readonly renderer: Renderer2) {}

  public ngAfterViewInit(): void {
    this.updateVisibleElements();
    this.listenToScrollEvents();
    this.listenToCarouselItemClick();
  }

  private listenToCarouselItemClick() {
    if (this.carouselContainer) {
      const container = this.carouselContainer.nativeElement;
      Array.from(container.children).forEach(this.registerItemClickCallback);
    }
  }

  private readonly registerItemClickCallback = (child: Element): void => {
    const unlistenFn = this.renderer.listen(
      child,
      'click',
      this.listenItemClickCallback(child)
    );

    this.unlistenItemClickFns.push(unlistenFn);
  };

  private readonly listenItemClickCallback = (child: Element): (() => void) => {
    return () => {
      if (child.parentElement) {
        const rect = child.parentElement.getBoundingClientRect();
        const childRect = child.getBoundingClientRect();

        if (childRect.left < rect.left || childRect.right > rect.right) {
          this.scrollToElement(child);
        }
      }
    };
  };

  @HostListener('window:resize')
  public listenToResizeEvents(): void {
    this.updateVisibleElements();
  }

  private listenToScrollEvents(): void {
    if (this.carouselContainer) {
      this.unlistenScrollFn = this.renderer.listen(
        this.carouselContainer.nativeElement,
        'scroll',
        this.updateVisibleElements
      );
    }
  }

  public ngOnDestroy(): void {
    if (this.unlistenScrollFn) {
      this.unlistenScrollFn();
    }

    if (this.unlistenItemClickFns.length > 0) {
      this.unlistenItemClickFns.forEach((unlistenFn) => unlistenFn());
    }
  }

  protected hasItemsOnLeft(): boolean {
    return this.visibleElements.length > 0 && this.nextLeftElement() !== null;
  }

  protected hasItemsOnRight(): boolean {
    return this.visibleElements.length > 0 && this.nextRightElement() !== null;
  }

  private readonly updateVisibleElements = (): void => {
    if (this.carouselContainer) {
      const element = this.carouselContainer.nativeElement;
      const rect = element.getBoundingClientRect();

      this.visibleElements = Array.from(element.children).filter((child) => {
        const childRect = child.getBoundingClientRect();

        return (
          childRect.left >= rect.left &&
          childRect.right - this.GAP_OFFSET <= rect.right - element.offsetLeft
        );
      });
    }
  };

  private nextLeftElement(): Element | null {
    return this.visibleElements[0]?.previousElementSibling;
  }

  private nextRightElement(): Element | null {
    const index = this.visibleElements.length - 1;
    return this.visibleElements[index]?.nextElementSibling;
  }

  protected scrollLeft(): void {
    this.scrollToElement(this.nextLeftElement());
  }

  protected scrollRight(): void {
    this.scrollToElement(this.nextRightElement());
  }

  private scrollToElement(nextElement: Element | null): void {
    if (!this.carouselContainer || !nextElement) {
      return;
    }

    const containerElement = this.carouselContainer.nativeElement;
    const newScrollPosition =
      (nextElement as HTMLElement).offsetLeft +
      this.GAP_OFFSET +
      nextElement.getBoundingClientRect().width / 2 -
      containerElement.getBoundingClientRect().width / 2;

    containerElement.scroll({
      left: newScrollPosition,
      behavior: 'smooth',
    });
  }
}
